import { useEffect, useState } from "react";
import http from "services/https";
import { ConditionsCondition } from "./conditions-condition";

export default function ConditionsEditor({
  initialConditions,
  onUpdate,
  defaultSuggestion = "url",
}) {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [data, setData] = useState({ definitions: [], fields: [] });
  const [defaults, setDefaults] = useState();
  const [conditions, setConditions] = useState();
  const [errors, setErrors] = useState({ hasError: false, errors: [] });
  const initialConditionsValid =
    typeof initialConditions === "object" && initialConditions.root;

  useEffect(() => {
    if (initialConditionsValid) {
      setConditions(cloneConditions(initialConditions));
    }
  }, [initialConditions, initialConditionsValid]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get(`campaign-conditions`);
        setData({ definitions: data.types, fields: data.fields });
        setDefaults(data.default);
      } catch (e) {
        console.error(e);
        setError("error loading conditions");
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (defaults && !initialConditionsValid) {
      const newConditions = cloneConditions(defaults);
      setConditions(newConditions);
      onUpdate(newConditions);
      setEditing(true);
    }
  }, [defaults, initialConditionsValid, onUpdate, editing]);

  const update = (params, path = []) => {
    if (!conditions) {
      return;
    }

    const newConditions = cloneConditions(conditions);

    let lastNode = newConditions.root;

    switch (params.action) {
      case "add":
        for (const idx of path) {
          lastNode = lastNode.data.children[idx];
        }
        const definition = data.definitions[params.type];
        lastNode.data.children.push({
          type: params.type,
          data: getDefaultData(
            params.type,
            definition,
            definition && data.fields[definition.type]
          ),
        });
        break;
      case "remove":
        for (let i = 0; i < path.length; i++) {
          const idx = path[i];
          if (i >= path.length - 1) {
            lastNode.data.children.splice(idx, 1);
            break;
          }
          lastNode = lastNode.data.children[idx];
        }
        break;
      case "edit":
        for (const idx of path) {
          lastNode = lastNode.data.children[idx];
        }
        lastNode.data[params.key] = params.value;
        break;
      default:
        console.warn("invalid action", params, path);
        return;
    }

    setErrors(validateConditions(newConditions.root));
    setConditions(newConditions);
    onUpdate(newConditions);
  };

  return (
    <div className="w-full">
      {error && <p className="text-red mb-2">{error}</p>}
      {loading || !conditions ? (
        <p>Loading...</p>
      ) : (
        <>
          <ConditionsCondition
            data={data}
            condition={conditions.root}
            errors={errors}
            update={update}
            defaultSuggestion={defaultSuggestion}
            editing={editing}
          />
          <button
            className="mt-2 bg-primary text-white p-4 font-bold rounded uppercase leading-none transition-colors hover:bg-primaryHover block w-full"
            onClick={(e) => {
              e.preventDefault();
              setEditing(!editing);
            }}
          >
            {editing ? "Done" : "Edit"}
          </button>
        </>
      )}
    </div>
  );
}

function cloneConditions(initial) {
  return {
    ...initial,
    root: cloneCondition(initial.root),
  };
}

function cloneCondition(node) {
  if (node.type === "group") {
    return {
      ...node,
      data: {
        ...node.data,
        children: node.data.children.map((x) => cloneCondition(x)),
      },
    };
  }

  return {
    ...node,
    data: {
      ...node.data,
    },
  };
}

function validateConditions(condition) {
  let hasError = false;

  const process = (condition) => {
    const errors = [];
    switch (condition.type) {
      case "group":
        return condition.data.children.map((x) => process(x));
      case "utm":
        // if (!condition.data.value) {
        //   errors.push("missing value");
        // }
        break;
      case "custom":
        if (!condition.data.param) {
          errors.push("missing param");
        }
        break;
      case "referral_domain":
        if (!condition.data.domain) {
          errors.push("missing domain");
        } else {
          try {
            new URL(condition.data.domain);
          } catch (e) {
            errors.push("invalid domain");
          }
        }
        break;
      case "url":
        if (!condition.data.value) {
          errors.push("missing url");
        }
        break;
      case "path":
        if (!condition.data.value) {
          errors.push("missing path");
        }
        break;
      case "returning":
      case "date_range":
      case "time_range":
      case "day":
      default:
    }

    if (condition.data.comparison === "regex") {
      try {
        new RegExp(condition.data.value);
      } catch (e) {
        errors.push("invalid regex");
      }
    }

    if (errors.length > 0) {
      hasError = true;
    }
    return errors;
  };

  const errors = process(condition);

  return { hasError, errors };
}

function getDefaultData(type, definition, fieldSet) {
  if (type === "group") {
    return {
      mode: "AND",
      children: [],
    };
  }

  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = new Date().toISOString().substring(0, 10);

  const data = {};

  Object.entries(fieldSet).forEach(([k, v]) => {
    if (v.type === "param") {
      if (definition.options) {
        data[k] = definition.options[0];
        return;
      }

      data[k] = "";
      return;
    }

    let value = v.default;
    if (value === "{today}") {
      value = today;
    } else if (value === "{currentTimezone}") {
      value = currentTimezone;
    }

    data[k] = value;
  });

  return data;
}
